import {truncateNumber} from "../../../../Utils/NumberUtil";
import {
    buildAxisTooltipFormatter,
    buildOverlappedBarSeries,
    ChartValue,
    OverlappedBarSeries
} from "../../../../Utils/OverlappedBarSeriesUtils";

// Visão Diária - gráfico Energia Gerada por usina (SGD-3816).

export interface PowerStationDailyGeneration {
    powerStationId: number;
    powerStationName: string;
    generatedEnergy: number | null;
    injectedEnergy: number | null;
    expectedInjectedEnergy: number | null;
    expectedEnergyCorrectedByIrradiation: number | null;
    performanceRatio: number | null;
    expectedPerformanceRatio: number | null;
}

export type DailyGenerationSortCriterion = "performanceRatio" | "generatedEnergy" | "powerStationName";
export type DailyGenerationSortDirection = "asc" | "desc";

export const DEFAULT_SORT_CRITERION: DailyGenerationSortCriterion = "performanceRatio";
export const DEFAULT_SORT_DIRECTION: DailyGenerationSortDirection = "asc";

// Coluna do grid da Visão Diária equivalente a cada critério do gráfico (RN 3.e). SGD-3816.
export const GRID_SORT_COLUMN_BY_CRITERION: Record<DailyGenerationSortCriterion, string> = {
    performanceRatio: "performanceRatio",
    generatedEnergy: "generatedEnergySum",
    powerStationName: "powerStationName",
};

export const VISIBLE_POWER_STATIONS = 24;
const AXIS_LABEL_MAX_LENGTH = 14;
const ENERGY_AXIS_MARGIN = 1.15;
const AXIS_SPLITS = 4;
const PERCENT_AXIS_MIN_MAX = 100;
const PERCENT_AXIS_STEP = 25;
// Linhas acima das barras sobrepostas (camadas usam z 2..5).
const LINE_Z = 10;

export const ENERGY_UNIT = "MWh";
export const PERCENT_UNIT = "%";
export const PR_SERIES_NAME = "PR";
export const EXPECTED_PR_SERIES_NAME = "PR esperado";
const PR_COLOR = "#F57C00";
const EXPECTED_PR_COLOR = "#FFB74D";

// Ordem, nomes e cores conforme Figma; mesmas cores das barras de Energia Gerada das Visões Usina/Skid.
export const ENERGY_SERIES: Array<{ name: string; color: string; getValue: (item: PowerStationDailyGeneration) => number | null }> = [
    {name: "Energia esperada corrigida", color: "#C5D8EE", getValue: (item) => item.expectedEnergyCorrectedByIrradiation},
    {name: "Energia esperada", color: "#4A6FA5", getValue: (item) => item.expectedInjectedEnergy},
    {name: "Energia gerada", color: "#2D5DE7", getValue: (item) => item.generatedEnergy},
    {name: "Energia injetada", color: "#1BB483", getValue: (item) => item.injectedEnergy},
];

// Só nulo vira '-'; zero é valor (padrão do gráfico do Diário de Operação e do grid).
export const toChartValue = (value: number | null | undefined): ChartValue =>
    value === null || value === undefined || Number.isNaN(value) ? "-" : truncateNumber(value, 2);

const compareNames = (a: PowerStationDailyGeneration, b: PowerStationDailyGeneration): number =>
    (a.powerStationName ?? "").localeCompare(b.powerStationName ?? "", "pt-BR", {sensitivity: "base"});

/**
 * Ordena as usinas do eixo X. Nulo no critério conta como zero; empate desempata pelo nome (A-Z),
 * qualquer que seja a direção. Não altera a lista recebida.
 */
export function sortDailyGeneration(
    items: PowerStationDailyGeneration[],
    criterion: DailyGenerationSortCriterion,
    direction: DailyGenerationSortDirection
): PowerStationDailyGeneration[] {
    const factor = direction === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
        if (criterion === "powerStationName") {
            return factor * compareNames(a, b);
        }
        const diff = (a[criterion] ?? 0) - (b[criterion] ?? 0);
        return diff !== 0 ? factor * diff : compareNames(a, b);
    });
}

export const formatPowerStationAxisLabel = (name: string): string =>
    name && name.length > AXIS_LABEL_MAX_LENGTH ? `${name.substring(0, AXIS_LABEL_MAX_LENGTH)}…` : name;

// Barra de zoom acima da legenda (mesmo padrão do gráfico Telecomandos por Usina da Auditoria).
export const DATA_ZOOM_SLIDER_HEIGHT = 18;
export const DATA_ZOOM_SLIDER_BOTTOM = 30;

/**
 * Até 24 usinas visíveis de início (na ordenação vigente). Com mais usinas: barra de zoom (slider) para
 * ampliar/reduzir a janela e scroll horizontal interno (roda do mouse e arrastar movem, não dão zoom).
 */
export function buildDailyGenerationDataZoom(total: number) {
    if (total <= VISIBLE_POWER_STATIONS) {
        return [];
    }
    const window = {xAxisIndex: 0, startValue: 0, endValue: VISIBLE_POWER_STATIONS - 1};
    return [
        {type: "slider", ...window, height: DATA_ZOOM_SLIDER_HEIGHT, bottom: DATA_ZOOM_SLIDER_BOTTOM, showDetail: false},
        {type: "inside", ...window, zoomOnMouseWheel: false, moveOnMouseWheel: true, moveOnMouseMove: true},
    ];
}

export function calculateEnergyAxisMax(items: PowerStationDailyGeneration[]): number {
    const values = items
        .flatMap((item) => ENERGY_SERIES.map((energy) => energy.getValue(item)))
        .filter((value): value is number => typeof value === "number" && !Number.isNaN(value));
    const max = values.length > 0 ? Math.max(...values) : 0;
    if (max <= 0) {
        return AXIS_SPLITS;
    }
    // Margem para nenhuma barra encostar no topo, arredondada para dividir o eixo em partes iguais.
    return Math.ceil(max * ENERGY_AXIS_MARGIN / AXIS_SPLITS) * AXIS_SPLITS;
}

export function calculatePercentAxisMax(items: PowerStationDailyGeneration[]): number {
    const values = items
        .flatMap((item) => [item.performanceRatio, item.expectedPerformanceRatio])
        .filter((value): value is number => typeof value === "number" && !Number.isNaN(value));
    const max = values.length > 0 ? Math.max(...values) : 0;
    return Math.max(PERCENT_AXIS_MIN_MAX, Math.ceil(max / PERCENT_AXIS_STEP) * PERCENT_AXIS_STEP);
}

export interface DailyGenerationLineSeries {
    name: string;
    type: "line";
    yAxisIndex: number;
    z: number;
    symbol: string;
    symbolSize: number;
    itemStyle: { color: string };
    lineStyle: { color: string; width: number; type?: string };
    data: ChartValue[];
}

const buildPrLine = (name: string, color: string, dashed: boolean, data: ChartValue[]): DailyGenerationLineSeries => ({
    name,
    type: "line",
    yAxisIndex: 1,
    z: LINE_Z,
    symbol: "emptyCircle",
    symbolSize: 7,
    itemStyle: {color},
    lineStyle: dashed ? {color, width: 2, type: "dashed"} : {color, width: 2},
    data,
});

// Ordem das séries = ordem da legenda: 4 energias (barras sobrepostas), PR esperado e PR.
export function buildDailyGenerationSeries(items: PowerStationDailyGeneration[]): Array<OverlappedBarSeries | DailyGenerationLineSeries> {
    const energyBars = buildOverlappedBarSeries(ENERGY_SERIES.map((energy) => ({
        name: energy.name,
        color: energy.color,
        values: items.map((item) => toChartValue(energy.getValue(item))),
    })));
    return [
        ...energyBars,
        buildPrLine(EXPECTED_PR_SERIES_NAME, EXPECTED_PR_COLOR, true, items.map((item) => toChartValue(item.expectedPerformanceRatio))),
        buildPrLine(PR_SERIES_NAME, PR_COLOR, false, items.map((item) => toChartValue(item.performanceRatio))),
    ];
}

export const dailyGenerationTooltipFormatter = buildAxisTooltipFormatter([
    ...ENERGY_SERIES.map((energy) => ({name: energy.name, unit: ENERGY_UNIT})),
    {name: EXPECTED_PR_SERIES_NAME, unit: PERCENT_UNIT},
    {name: PR_SERIES_NAME, unit: PERCENT_UNIT},
]);
